import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material'
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material'
import {
  PaymentBatch,
  ProviderPayout,
  formatCurrency,
  getInitials,
} from '../mockData'

interface ProcessPaymentModalProps {
  open: boolean
  onClose: () => void
  batch: PaymentBatch
  onProcess: (status: string) => void
}

const PREVIEW_COUNT = 4

const STATUS_OPTIONS = ['Pending', 'Paid', 'Cancelled']

const ProcessPaymentModal: React.FC<ProcessPaymentModalProps> = ({
  open,
  onClose,
  batch,
  onProcess,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<string>(() =>
    STATUS_OPTIONS.includes(batch.status) ? batch.status : 'Pending'
  )

  // Keep the dropdown in sync with the batch's current status when reopened.
  useEffect(() => {
    if (open) {
      setSelectedStatus(
        STATUS_OPTIONS.includes(batch.status) ? batch.status : 'Pending'
      )
    }
  }, [open, batch.status])

  const isClaim = batch.batchType === 'claim'
  const itemLabel = isClaim ? 'claims' : 'invoices'
  const itemLabelCap = isClaim ? 'Claims' : 'Invoices'
  const validationItems = [
    `All selected ${itemLabel} approved`,
    'No duplicate payouts detected',
    'Agency authorization confirmed',
  ]

  const previewProviders = batch.providers.slice(0, PREVIEW_COUNT)
  const remaining = batch.providers.slice(PREVIEW_COUNT)
  const remainingAmount = remaining.reduce(
    (sum, p) => sum + p.payableAmount,
    0
  )

  const handleConfirm = () => {
    setConfirmOpen(false)
    onProcess(selectedStatus)
  }

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box sx={{ p: 3, pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
          Review provider payouts before processing payments.
        </Typography>
      </Box>

      <DialogContent>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2.5,
            mb: 2.5,
          }}
        >
          {/* Batch summary */}
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2.5 }}>
            <SectionLabel>BATCH SUMMARY</SectionLabel>
            <SummaryRow label="Batch Name" value={batch.name} />
            <SummaryRow label="Batch Month" value={batch.month} />
            <SummaryRow
              label="Total Providers"
              value={String(batch.totalProviders)}
            />
            <SummaryRow
              label={`Total ${itemLabelCap}`}
              value={String(batch.totalInvoices)}
            />
            <SummaryRow
              label="Total Payout Amount"
              value={formatCurrency(batch.totalPayout)}
              highlight
            />
          </Box>

          {/* Validation */}
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2.5 }}>
            <SectionLabel>VALIDATION</SectionLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {validationItems.map(item => (
                <Box
                  key={item}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: '#ecfdf5',
                    border: '1px solid #d1fae5',
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <CheckCircleIcon
                    fontSize="small"
                    sx={{ color: '#10b981' }}
                  />
                  <Typography variant="body2" sx={{ color: '#065f46' }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Payout table */}
        <Box
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#7A6FA1' }}>
                {['Provider', 'Sessions', isClaim ? 'Claim Count' : 'Invoice Count', 'Payout Amount', 'Status'].map(
                  head => (
                    <TableCell
                      key={head}
                      sx={{ color: 'white', fontWeight: 600 }}
                    >
                      {head}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewProviders.map(provider => (
                <ProviderRow key={provider.providerId} provider={provider} />
              ))}
              {remaining.length > 0 && (
                <TableRow>
                  <TableCell sx={{ color: '#64748b', fontStyle: 'italic' }}>
                    + {remaining.length} more providers
                  </TableCell>
                  <TableCell sx={{ color: '#cbd5e1' }}>—</TableCell>
                  <TableCell sx={{ color: '#cbd5e1' }}>—</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {formatCurrency(remainingAmount)}
                  </TableCell>
                  <TableCell sx={{ color: '#cbd5e1' }}>—</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ p: 2.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => setConfirmOpen(true)}
          variant="contained"
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            bgcolor: '#7A6FA1',
            '&:hover': { bgcolor: '#655a8a' },
          }}
        >
          Process Status
        </Button>
      </DialogActions>
    </Dialog>

    {/* Confirmation before processing payout status */}
    <Dialog
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        Confirm payout status update
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2.5 }}>
          You are about to update the payout status for{' '}
          <strong>{batch.totalProviders} providers</strong> (
          {batch.totalInvoices} {itemLabel},{' '}
          <strong>{formatCurrency(batch.totalPayout)}</strong>). You can change
          this status at any time.
        </DialogContentText>
        <FormControl fullWidth size="small">
          <InputLabel id="payout-status-label">Payout status</InputLabel>
          <Select
            labelId="payout-status-label"
            label="Payout status"
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
            sx={{ borderRadius: 2 }}
          >
            {STATUS_OPTIONS.map(status => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button
          onClick={() => setConfirmOpen(false)}
          variant="outlined"
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            bgcolor: '#7A6FA1',
            '&:hover': { bgcolor: '#655a8a' },
          }}
        >
          Yes, Update Status
        </Button>
      </DialogActions>
    </Dialog>
    </>
  )
}

const ProviderRow: React.FC<{ provider: ProviderPayout }> = ({ provider }) => (
  <TableRow>
    <TableCell>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 30, height: 30, fontSize: 13, bgcolor: '#ede9fe', color: '#7A6FA1' }}>
          {getInitials(provider.name)}
        </Avatar>
        <Typography variant="body2">{provider.name}</Typography>
      </Box>
    </TableCell>
    <TableCell>{provider.sessions}</TableCell>
    <TableCell>{provider.invoiceCount}</TableCell>
    <TableCell sx={{ fontWeight: 700 }}>
      {formatCurrency(provider.payableAmount)}
    </TableCell>
    <TableCell>
      <Chip
        label={provider.status}
        size="small"
        sx={{ ...statusChipSx(provider.status), fontWeight: 500 }}
      />
    </TableCell>
  </TableRow>
)

const statusChipSx = (status: string) =>
  status === 'Paid'
    ? { bgcolor: '#dcfce7', color: '#166534' }
    : { bgcolor: '#fef3c7', color: '#92400e' }

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      fontWeight: 700,
      letterSpacing: '0.06em',
      color: '#64748b',
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
)

const SummaryRow: React.FC<{
  label: string
  value: string
  highlight?: boolean
}> = ({ label, value, highlight }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.85,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 700, color: highlight ? '#7A6FA1' : '#1e293b' }}
    >
      {value}
    </Typography>
  </Box>
)

export default ProcessPaymentModal
